import { Outlet, useNavigate } from 'react-router-dom'
import { Box, Button, CircularProgress } from '@mui/material'
import { CreateNewFolder, ErrorOutline } from '@mui/icons-material'
import { appPath } from '@yourownmetric/shared'

import { useListProjectsQuery } from '@/apis/projectsApi'
import { useGetProjectConfigQuery } from '@/apis/v2Api'
import { useAppSelector } from '@/app/hooks'

import { EmptyState } from './EmptyState'

/**
 * Report routes render only with a project of THIS account selected.
 * The selection itself is validated in DashboardLayout; here we show
 * loading, failure and the "no project yet" state.
 */
export function ProjectGate() {
    const navigate = useNavigate()
    const selectedId = useAppSelector(s => s.project.selectedId)
    const { data: projects, isLoading, isError, refetch } = useListProjectsQuery()
    const valid = !!selectedId && !!projects?.some(p => p.id === selectedId)
    // Reports use the project's timezone for their dates: load its config first
    // (a failure falls back to the browser's timezone).
    const config = useGetProjectConfigQuery(selectedId ?? '', { skip: !valid })

    if (isLoading) {
        return (
            <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
            </Box>
        )
    }
    if (isError || !projects) {
        return (
            <Box sx={{ p: 3 }}>
                <EmptyState
                    icon={<ErrorOutline />}
                    title="Couldn't load your projects"
                    description="Check your connection and try again."
                    action={
                        <Button variant="outlined" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                />
            </Box>
        )
    }
    if (projects.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <EmptyState
                    icon={<CreateNewFolder />}
                    title="Create your first project"
                    description="Your organization has no projects yet. A project holds the keys, settings and analytics for one site or app."
                    actionLabel="Create project"
                    onAction={() => navigate(appPath('projects?new=1'))}
                />
            </Box>
        )
    }
    if (!valid || (config.currentData === undefined && !config.isError)) {
        // DashboardLayout is switching to a valid project
        return (
            <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
            </Box>
        )
    }
    return <Outlet />
}
